/**
 * EEOC Voice Assessment Component
 * Voice-based interview simulation for EEOC compliance training
 * Shared between Mock Test and Final Assessment
 * 
 * Features:
 * - Two-way verbal communication
 * - Speech-to-Text using LemonFox (backend)
 * - Text-to-Speech using Web Speech API (free, browser-based)
 * - Fallback text input for users without microphone
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Mic, MicOff, Volume2, VolumeX, Send, AlertTriangle, 
  ArrowLeft, CheckCircle2, Clock, User, UserCircle, Bot,
  Loader2, Shield, Keyboard, Settings2, PlayCircle, StopCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Web Speech API TTS helper
const speak = (text, onEnd, rate = 1.0, pitch = 1.0) => {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) {
      console.warn('Web Speech API not supported');
      resolve();
      return;
    }
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = 1;
    
    // Try to use a natural female voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Samantha') || 
      v.name.includes('Google US English Female') ||
      v.name.includes('Microsoft Zira') ||
      (v.lang.startsWith('en') && v.name.toLowerCase().includes('female'))
    ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.onend = () => {
      if (onEnd) onEnd();
      resolve();
    };
    utterance.onerror = () => {
      if (onEnd) onEnd();
      resolve();
    };
    
    window.speechSynthesis.speak(utterance);
  });
};

// Stop speaking
const stopSpeaking = () => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

export default function EEOCVoiceAssessment({ isMock = false }) {
  const { linkId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [testStarted, setTestStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const [finishing, setFinishing] = useState(false);
  
  // Voice settings
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [textInputMode, setTextInputMode] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [speechRate, setSpeechRate] = useState(1.0);
  const [showSettings, setShowSettings] = useState(false);
  
  // Audio refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  
  const minExchanges = isMock ? 3 : 6;
  const progress = Math.min((exchangeCount / minExchanges) * 100, 100);
  
  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load voices when component mounts
  useEffect(() => {
    // Load voices (some browsers load them asynchronously)
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    
    fetchTestData();
    
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      stopSpeaking();
    };
  }, [linkId]);

  const fetchTestData = async () => {
    try {
      const response = await api.get(`/eeoc/test/${linkId}`);
      setTestData(response.data);
    } catch (err) {
      toast.error('Test not found');
      navigate(`/eeoc/test/${linkId}`);
    } finally {
      setLoading(false);
    }
  };

  const startTest = async () => {
    setStarting(true);
    try {
      const response = await api.post('/eeoc/test/start', {
        link_id: linkId,
        is_mock: isMock
      });
      
      setTestStarted(true);
      
      // Add candidate greeting
      const greeting = response.data.candidate_greeting;
      setMessages([{
        role: 'candidate',
        content: greeting,
        timestamp: new Date().toISOString()
      }]);
      
      // Use Web Speech API for TTS
      if (voiceEnabled) {
        setIsSpeaking(true);
        await speak(greeting, () => setIsSpeaking(false), speechRate);
      }
      
    } catch (err) {
      toast.error('Failed to start test');
    } finally {
      setStarting(false);
    }
  };

  // Speak a message using Web Speech API
  const speakMessage = useCallback(async (text) => {
    if (!voiceEnabled) return;
    
    setIsSpeaking(true);
    await speak(text, () => setIsSpeaking(false), speechRate);
  }, [voiceEnabled, speechRate]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAndSend(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording... Speak now', { duration: 2000 });
    } catch (err) {
      toast.error('Microphone access denied. Please enable microphone permissions or use text input.');
      setTextInputMode(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAndSend = async (audioBlob) => {
    setIsProcessing(true);
    try {
      // Transcribe audio using backend (LemonFox)
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const transcribeRes = await api.post('/eeoc/audio/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const userMessage = transcribeRes.data.text;
      
      if (!userMessage || userMessage.trim().length < 3) {
        toast.error('Could not understand. Please try again or use text input.');
        setIsProcessing(false);
        return;
      }
      
      await sendMessage(userMessage);
      
    } catch (err) {
      console.error('Transcription error:', err);
      toast.error('Failed to process audio. Please try again or use text input.');
      setTextInputMode(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Send message (from voice or text)
  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;
    
    setIsProcessing(true);
    
    try {
      // Add user message to chat
      const newMessages = [...messages, {
        role: 'recruiter',
        content: messageText,
        timestamp: new Date().toISOString()
      }];
      setMessages(newMessages);
      setTextInput('');
      
      // Get AI candidate response
      const chatRes = await api.post('/eeoc/chat', {
        link_id: linkId,
        message: messageText,
        is_mock: isMock,
        conversation_history: newMessages.map(m => ({
          role: m.role === 'recruiter' ? 'user' : 'assistant',
          content: m.content
        }))
      });
      
      const responseText = chatRes.data.response;
      
      // Add candidate response
      setMessages(prev => [...prev, {
        role: 'candidate',
        content: responseText,
        timestamp: new Date().toISOString()
      }]);
      
      setExchangeCount(prev => prev + 1);
      
      // Use Web Speech API for TTS
      await speakMessage(responseText);
      
    } catch (err) {
      console.error('Error:', err);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle text input submit
  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (textInput.trim() && !isProcessing && !isSpeaking) {
      sendMessage(textInput);
    }
  };

  const finishTest = async () => {
    if (isMock) {
      toast.success('Practice complete! Now try the final assessment.');
      navigate(`/eeoc/test/${linkId}`);
      return;
    }
    
    setFinishing(true);
    try {
      await api.post(`/eeoc/test/evaluate/${linkId}`);
      toast.success('Assessment evaluated!');
      navigate(`/eeoc/test/${linkId}/results`);
    } catch (err) {
      toast.error('Failed to evaluate. Please try again.');
      setFinishing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col" data-testid="eeoc-assessment">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(`/eeoc/test/${linkId}`)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="w-10 h-10 bg-slate-900 dark:bg-slate-100 rounded-md flex items-center justify-center">
                <Shield className="w-5 h-5 text-white dark:text-slate-900" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {isMock ? 'Practice Test' : 'Final Assessment'}
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">{testData?.user_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {isMock && (
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  Practice Mode
                </Badge>
              )}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {exchangeCount}/{minExchanges} exchanges
                </span>
              </div>
            </div>
          </div>
          
          {testStarted && (
            <div className="mt-3">
              <Progress value={progress} className="h-2" />
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-6">
        {!testStarted ? (
          <Card className="max-w-lg mx-auto">
            <CardHeader>
              <CardTitle>{isMock ? 'Ready for Practice?' : 'Ready to Begin?'}</CardTitle>
              <CardDescription>
                {isMock 
                  ? 'Practice verbal interactions with AI candidates in a no-stakes environment.'
                  : 'Your verbal responses will be recorded and evaluated for EEOC compliance.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-800/50 dark:to-blue-900/20 rounded-lg space-y-3 border border-slate-200 dark:border-slate-700">
                <h4 className="font-medium text-slate-900 dark:text-white text-sm">How It Works:</h4>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-emerald-600">1</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">AI Candidate Speaks First</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">The candidate will greet you and introduce themselves verbally</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">You Respond by Speaking</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Click the microphone and speak your interview questions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-purple-600">3</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">Two-Way Conversation</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Continue the verbal dialogue until assessment is complete</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <Mic className="h-5 w-5 text-blue-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Microphone required (text fallback available)
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Volume2 className="h-5 w-5 text-emerald-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Candidate will speak using your browser's voice
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-slate-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {isMock ? '~10 minutes (3+ exchanges)' : '~20-30 minutes (6+ exchanges)'}
                  </span>
                </div>
              </div>
              
              <Button 
                onClick={startTest}
                disabled={starting}
                className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900"
                data-testid="begin-test-btn"
              >
                {starting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Starting...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Begin Verbal Interview
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Chat Messages */}
            <Card className="h-[400px] flex flex-col">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Interview Conversation</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-4">
                    {messages.map((msg, idx) => (
                      <div 
                        key={idx}
                        className={cn(
                          "flex gap-3",
                          msg.role === 'recruiter' ? 'justify-end' : 'justify-start'
                        )}
                      >
                        {msg.role !== 'recruiter' && (
                          <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          </div>
                        )}
                        <div
                          className={cn(
                            "max-w-[80%] rounded-lg px-4 py-2",
                            msg.role === 'recruiter'
                              ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                          )}
                        >
                          <p className="text-sm">{msg.content}</p>
                        </div>
                        {msg.role === 'recruiter' && (
                          <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                            <UserCircle className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {(isProcessing || isSpeaking) && (
                      <div className="flex gap-3 justify-start">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg px-4 py-2">
                          <div className="flex items-center gap-2">
                            {isProcessing ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm text-slate-500">Thinking...</span>
                              </>
                            ) : (
                              <>
                                <Volume2 className="w-4 h-4 animate-pulse text-emerald-600" />
                                <span className="text-sm text-slate-500">Speaking...</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Recording Controls */}
            <Card>
              <CardContent className="pt-6">
                {/* Voice/Text Toggle & Settings */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="voice-mode"
                        checked={!textInputMode}
                        onCheckedChange={(checked) => setTextInputMode(!checked)}
                        data-testid="voice-mode-toggle"
                      />
                      <Label htmlFor="voice-mode" className="text-sm">
                        {textInputMode ? 'Text Mode' : 'Voice Mode'}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setVoiceEnabled(!voiceEnabled)}
                        className={cn(!voiceEnabled && 'text-muted-foreground')}
                        data-testid="voice-enabled-toggle"
                      >
                        {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {voiceEnabled ? 'Audio On' : 'Audio Off'}
                      </span>
                    </div>
                  </div>
                  
                  {isSpeaking && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        stopSpeaking();
                        setIsSpeaking(false);
                      }}
                      className="text-red-600"
                      data-testid="stop-speaking-btn"
                    >
                      <StopCircle className="h-4 w-4 mr-1" />
                      Stop
                    </Button>
                  )}
                </div>

                {textInputMode ? (
                  /* Text Input Mode */
                  <form onSubmit={handleTextSubmit} className="space-y-4">
                    <div className="flex gap-2">
                      <Textarea
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder="Type your response to the candidate..."
                        disabled={isProcessing || isSpeaking}
                        className="flex-1 min-h-[100px] resize-none"
                        data-testid="text-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!textInput.trim() || isProcessing || isSpeaking}
                      className="w-full"
                      data-testid="send-text-btn"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Response
                        </>
                      )}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      <Keyboard className="inline h-3 w-3 mr-1" />
                      Text mode active - Type your response and click Send
                    </p>
                  </form>
                ) : (
                  /* Voice Input Mode */
                  <>
                    <div className="flex items-center justify-center gap-4">
                      <Button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={isProcessing || isSpeaking}
                        variant={isRecording ? 'destructive' : 'default'}
                        size="lg"
                        className={cn(
                          "h-20 w-20 rounded-full transition-all",
                          isRecording && "animate-pulse scale-110 shadow-lg shadow-red-500/30",
                          !isRecording && !isProcessing && !isSpeaking && "hover:scale-105"
                        )}
                        data-testid="record-btn"
                      >
                        {isRecording ? (
                          <MicOff className="h-8 w-8" />
                        ) : (
                          <Mic className="h-8 w-8" />
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
                      {isRecording 
                        ? '🔴 Recording... Click again to stop and send'
                        : isProcessing 
                          ? '⏳ Processing your response...'
                          : isSpeaking
                            ? '🔊 Candidate is speaking... Please wait'
                            : '🎤 Click the microphone to speak'}
                    </p>
                    
                    <p className="text-center text-xs text-muted-foreground mt-2">
                      Having trouble? <button 
                        type="button"
                        onClick={() => setTextInputMode(true)} 
                        className="text-blue-600 hover:underline"
                      >
                        Switch to text mode
                      </button>
                    </p>
                  </>
                )}
                
                {exchangeCount >= minExchanges && (
                  <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      <span className="text-sm text-emerald-600 dark:text-emerald-400">
                        Minimum exchanges completed!
                      </span>
                    </div>
                    <Button
                      onClick={finishTest}
                      disabled={finishing || isSpeaking}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="finish-test-btn"
                    >
                      {finishing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Evaluating...
                        </>
                      ) : isMock ? (
                        'Finish Practice'
                      ) : (
                        'Submit for Evaluation'
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tips Card */}
            {isMock && (
              <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Practice Tips</p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        Focus on job-related questions. When candidates volunteer personal info, 
                        redirect to job requirements. Avoid follow-up questions about protected categories.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
